import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Collapse,
  IconButton,
  Tooltip,
  Link as MuiLink,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

export interface BetterstackLink {
  label: string;
  url: string;
  source_id: string;
  description: string;
}

interface Props {
  links: BetterstackLink[];
}

const getServiceIcon = (label: string): string => {
  switch (label) {
    case "UnrealClient":
      return "🎮";
    case "Pixelstreaming SS":
      return "📡";
    case "Puppet":
      return "🤖";
    case "Pixels Scheduling":
      return "📅";
    case "Pixelstreaming Node Admin":
      return "🖥️";
    default:
      return "📋";
  }
};

const BetterstackLinksSection: React.FC<Props> = ({ links }) => {
  const [expanded, setExpanded] = useState(false);

  if (!links || links.length === 0) {
    return null;
  }

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "#f8f9fa",
          },
          p: 1,
          borderRadius: 1,
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
          Betterstack Log Links ({links.length})
        </Typography>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Stack spacing={1.5} sx={{ mt: 1, pl: 1 }}>
          {links.map((link, index) => (
            <Box
              key={index}
              sx={{
                p: 1.5,
                backgroundColor: "#f8f9fa",
                borderRadius: 1,
                border: "1px solid #e5e7eb",
                "&:hover": {
                  backgroundColor: "#e5e7eb",
                  borderColor: "#cbd5e1",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: "1.2rem" }}>{getServiceIcon(link.label)}</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {link.label}
                  </Typography>
                </Box>
                <Tooltip title="Open in new tab" placement="left">
                  <Button
                    component={MuiLink}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="outlined"
                    endIcon={<OpenInNewIcon sx={{ fontSize: "0.9rem" }} />}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      minWidth: "auto",
                      px: 1.5,
                      py: 0.5,
                    }}
                  >
                    Open Logs
                  </Button>
                </Tooltip>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {link.description}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.65rem",
                  color: "#6366f1",
                  display: "block",
                  mt: 0.5,
                }}
              >
                Source: {link.source_id}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
};

export default BetterstackLinksSection;
